import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './VerifyEmail.css';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<any>(null);

  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  useEffect(() => {
    if (!email || !token) {
      setError('Invalid verification link - missing email or token');
    }
  }, [email, token]);

  const verifyEmail = async () => {
    try {
      setVerifying(true);
      setError(null);

      const result = await apiService.verifyEmail(email, token);

      if (result.status === 'success') {
        setVerified(true);
        setCustomerData(result);
      } else {
        throw new Error(result.detail || 'Email verification failed');
      }
    } catch (error: any) {
      console.error('Verification error:', error);

      // Handle "Email already verified" as a special case
      if (error.message === 'Email already verified') {
        setVerified(true);
        setCustomerData({
          status: 'success',
          email: email,
          customer_id: email.toUpperCase(),
          credits_added: 0,
          verified: true,
          message: 'Your email was already verified! You can continue to use the platform.'
        });
      } else {
        setError(error.message || 'Failed to verify email');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleContinue = () => {
    if (customerData?.customer_id) {
      navigate(`/upload?customer_id=${customerData.customer_id}&verified=true`);
    } else {
      navigate('/pricing');
    }
  };

  if (verifying) {
    return (
      <div className="verify-email-page">
        <div className="verify-container">
          <div className="loading">
            <div className="spinner"></div>
            <h2>Verifying Your Email...</h2>
            <p>Please wait while we activate your account</p>
          </div>
        </div>
      </div>
    );
  }

  if (!verified && !error) {
    return (
      <div className="verify-email-page">
        <div className="verify-container">
          <div className="verify-confirm">
            <h2>Confirm Email Activation</h2>
            <p>Click the button below to activate your Proven account for <strong>{email}</strong>.</p>
            <div className="verify-actions">
              <button
                onClick={verifyEmail}
                className="btn primary large"
                disabled={!email || !token}
              >
                Confirm & Activate My Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="verify-email-page">
        <div className="verify-container">
          <div className="verify-error">
            <div className="error-icon">✗</div>
            <h2>Verification Failed</h2>
            <p className="error-message">{error}</p>
            
            <div className="error-actions">
              {error.includes('expired') && (
                <button 
                  onClick={() => navigate(`/verify-email-pending?email=${encodeURIComponent(email)}`)}
                  className="btn primary"
                >
                  Request New Verification Email
                </button>
              )}
              
              <button 
                onClick={() => navigate('/pricing')}
                className="btn secondary"
              >
                Back to Pricing
              </button>
            </div>

            <div className="support-help">
              <p>Need help? Contact our support team:</p>
              <a href="mailto:info@use-proven.com" className="support-link">
                📧 info@use-proven.com
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-email-page">
      <div className="verify-container">
        <div className="verify-success">
          <div className="success-icon">✓</div>
          
          <h1>Email Verified Successfully!</h1>
          
          <div className="success-details">
            <div className="welcome-box">
              <h3>Welcome{customerData?.credits_added > 0 ? '' : ' Back'} to Proven!</h3>
              {customerData?.credits_added > 0 ? (
                <>
                  <p>Your account has been created and activated with:</p>
                  <div className="credits-granted">
                    <div className="credits-amount">
                      <span className="credits-number">{customerData.credits_added}</span>
                      <span className="credits-label">Free Credits</span>
                    </div>
                    <div className="credits-value">
                      Worth ${(customerData.credits_added * 0.1).toFixed(0)} • {Math.floor(customerData.credits_added / 50)} Free Analyses
                    </div>
                  </div>
                </>
              ) : (
                <p>Your account is already active and ready to use. You can continue with your EUDR compliance analysis.</p>
              )}
            </div>

            {customerData?.credits_added > 0 && (
              <div className="password-info">
                <div className="info-box">
                  <h4>📧 Check Your Email</h4>
                  <p>Your temporary login password has been sent to your email address. Please check your inbox and use it to log in to your account.</p>
                </div>
              </div>
            )}
          </div>

          <div className="next-steps">
            <h4>What's Next?</h4>
            <div className="steps-list">
              <div className="step-item">
                <span className="step-number">1</span>
                <span>Upload your GeoJSON plantation data</span>
              </div>
              <div className="step-item">
                <span className="step-number">2</span>
                <span>Run EUDR compliance analysis</span>
              </div>
              <div className="step-item">
                <span className="step-number">3</span>
                <span>Download your compliance reports</span>
              </div>
            </div>
          </div>

          <div className="verify-actions">
            <button 
              onClick={handleContinue}
              className="btn primary large"
            >
              Start Your First Analysis
            </button>
            
            <button 
              onClick={() => navigate(`/dashboard?customer_id=${customerData?.customer_id}`)}
              className="btn secondary"
            >
              View Dashboard
            </button>
          </div>

          <div className="security-confirmed">
            <span className="security-icon">🔒</span>
            <p>Your email has been verified and your account is secure</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;